import * as React from 'react';
import { styled } from '@linaria/react';
import { Loader, X as XIcon, Check as CheckIcon } from 'lucide-react';

import TornEdge from './TornEdge';
import type { Render } from './DeferredValueTimeline.types';

interface Props {
  render: Render;
  currentFrame: number;
  totalNumOfFrames: number;
  startedByClick: boolean;
}

function RenderEvent({
  render,
  currentFrame,
  totalNumOfFrames,
  startedByClick,
}: Props) {
  const hasFinished = render.end <= currentFrame;
  const styles = getStyles(render, currentFrame);

  React.useEffect(() => {
    try {
      Object.entries(styles).forEach(([key, val]) => {
        CSS.registerProperty({
          name: key,
          syntax: '<color>',
          inherits: true,
          initialValue: val,
        });
      });
    } catch (err) {
      // CSS properties are global, which means we'll get an error when we try to register a property which is already registered. We can safely ignore it. Even if this *is* a genuine error, the worst case is that we don't get nice transitions between colors. Not the end of the world.
    }

    // ESLint wants us to add `styles` to the dep array, but we specifically only want to do this once, on mount, to set initial values.
    // eslint-disable-next-line
  }, []);

  const widthInFrames = render.end - render.start;
  const widthInPercentage = (widthInFrames / totalNumOfFrames) * 100;
  const startInPercentage = (render.start / totalNumOfFrames) * 100;

  const Wrapper = render.interrupted
    ? InterruptedWrapper
    : BaseWrapper;

  const widthOffset = startedByClick ? 5 : 2;
  const leftOffset = startedByClick ? 4 : 1;

  return (
    <Wrapper
      style={{
        ...styles,
        width: `calc(${widthInPercentage}% - ${widthOffset}px)`,
        left: `calc(${startInPercentage}% + ${leftOffset}px)`,
      }}
      data-has-committed={String(
        !!hasFinished && !render.interrupted
      )}
    >
      <Contents
        count={render.values.count}
        dCount={render.values.dCount}
      />

      <ResultWrapper>
        {hasFinished ? (
          render.interrupted ? (
            <ResultIconWrapper>
              <XIcon size={15} strokeWidth={3} />
            </ResultIconWrapper>
          ) : (
            <ResultIconWrapper>
              <CheckIcon size={15} strokeWidth={3} />
            </ResultIconWrapper>
          )
        ) : (
          <Loader
            size={15}
            strokeWidth={3}
            style={{
              transform: `rotate(${currentFrame * 8}deg)`,
            }}
          />
        )}
      </ResultWrapper>
      {render.interrupted && hasFinished && <TornEdge />}
    </Wrapper>
  );
}

function Contents({
  count,
  dCount,
}: {
  count: number;
  dCount?: number;
}) {
  return (
    <ContentsWrapper
      data-type={typeof dCount === 'number' ? 'double' : 'single'}
    >
      <Col>
        <Label>count</Label>
        <Value>{count}</Value>
      </Col>
      {typeof dCount === 'number' && (
        <>
          <ColSpacer />
          <Col>
            <Label>dCount</Label>
            <Value>{dCount}</Value>
          </Col>
        </>
      )}
    </ContentsWrapper>
  );
}

const getStyles = (render: Render, currentFrame: number) => {
  const hasFinished = render.end <= currentFrame;

  if (!hasFinished) {
    return {
      '--icon-color': 'hsl(210deg 8% 88%)',
      '--text-color': 'hsl(0deg 0% 100%)',
      '--border-color': 'hsl(210deg 8% 40%)',
      '--background-color': 'hsl(210deg 15% 30%)',
    };
  }

  if (hasFinished && render.interrupted) {
    return {
      '--icon-color': 'white',
      '--text-color': 'hsl(0deg 0% 100%)',
      '--border-color': 'hsl(345deg 62% 55%)',
      '--background-color': 'hsl(345deg 60% 35%)',
    };
  }

  return {
    '--icon-color': 'hsl(150deg 100% 30%)',
    '--text-color': 'hsl(0deg 0% 0%)',
    '--border-color': 'hsl(210deg 8% 100%)',
    '--background-color': 'hsl(210deg 25% 88%)',
  };
};

const CONDENSED_BREAKPOINT = {
  single: 55 / 16,
  double: 110 / 16,
};
const SUPERCONDENSED_BREAKPOINT = {
  double: 42 / 16,
};

const BaseWrapper = styled.div`
  --border-width: 2px;
  --highlight-curve: cubic-bezier(0.74, -0.01, 0.28, 1);

  position: absolute;
  display: flex;
  flex-direction: column;
  bottom: 4px;
  height: calc(100% - 8px);
  padding: var(--border-width);
  border-radius: 6px;
  background: var(--border-color);
  color: var(--text-color);
  font-family: var(--font-family-mono);
  font-size: 0.875rem;
  white-space: nowrap;
  container-type: inline-size;

  &[data-has-committed='true'] {
    animation: committedWrapper 600ms var(--highlight-curve);
  }
  &:not([data-has-committed='true']) {
    /* prettier-ignore */
    transition:
      --border-color 300ms,
      --background-color 300ms,
      --text-color 300ms;
  }

  @keyframes committedWrapper {
    from {
      background: hsl(150deg 100% 50%);
    }
  }
`;

const InterruptedWrapper = styled(BaseWrapper)`
  border-radius: 6px 0px 0px 6px;
  padding-right: 0px;
`;

const ResultWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 1.5rem;
  color: var(--icon-color);
`;

const ResultIconWrapper = styled.div`
  animation: fadeIn 400ms;
`;

const ContentWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  border-radius: 4px;
  background: var(--background-color);
  /* On *really* small viewports, the divider between top/bottom number overflows. Clip it so that it appears to fit perfectly. */
  overflow: hidden;
  overflow: clip;

  &::before {
    content: '';
    position: absolute;
    top: var(--border-width);
    left: var(--border-width);
    right: var(--border-width);
    height: 24px;
    border-radius: 2px 2px 0px 0px;
    background: linear-gradient(
      to bottom,
      var(--border-color),
      transparent
    );
    pointer-events: none;
    opacity: 0.3;
  }

  ${InterruptedWrapper} &::before {
    right: 0;
    border-radius: 2px 0px 0px 0px;
  }

  @container (max-width: ${SUPERCONDENSED_BREAKPOINT.double}rem) {
    &[data-type='double'] {
      flex-direction: column;
    }
  }

  ${BaseWrapper}[data-has-committed='true'] & {
    animation: committedChild 600ms var(--highlight-curve);
  }

  @keyframes committedChild {
    from {
      background: hsl(150deg 60% 50%);
    }
  }
`;

const ContentsWrapper = styled(ContentWrapper)`
  padding: 0 16px;

  @container (max-width: ${CONDENSED_BREAKPOINT.double}rem) {
    padding: 0;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @container (max-width: ${CONDENSED_BREAKPOINT.double}rem) {
    ${ContentWrapper}[data-type='double'] & {
      letter-spacing: 2px;
    }
  }
`;

const ColSpacer = styled.div`
  width: 24px;
  min-width: 8px;
  flex-shrink: 10000;

  @container (max-width: ${CONDENSED_BREAKPOINT.double}rem) {
    display: none;
  }
`;

const Label = styled.div`
  font-size: 0.8rem;
  margin-bottom: 0;

  @container (max-width: ${CONDENSED_BREAKPOINT.single}rem) {
    ${ContentWrapper}[data-type='single'] & {
      display: none;
    }
  }
  @container (max-width: ${CONDENSED_BREAKPOINT.double}rem) {
    ${ContentWrapper}[data-type='double'] & {
      display: none;
    }
  }
`;

const Value = styled.div`
  position: relative;
  font-size: 1rem;
  font-weight: var(--font-weight-bold);

  @container (max-width: ${CONDENSED_BREAKPOINT.double}rem) {
    ${ContentWrapper}[data-type='double'] ${Col}:last-child & {
      padding-left: 12px;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 4px;
        bottom: 0;
        width: 1.5px;
        height: 22px;
        margin-block: auto;
        background: var(--text-color);
        border-radius: 99px;
        opacity: 0.35;
      }
    }
  }

  @container (max-width: ${SUPERCONDENSED_BREAKPOINT.double}rem) {
    ${ContentWrapper}[data-type='double'] ${Col}:last-child & {
      padding-left: 0;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -4px;
        right: -4px;
        width: calc(100% + 8px);
        height: 1.5px;
        margin-block: revert;
        background: var(--text-color);
      }
    }
  }
`;

export default RenderEvent;
